import { useContext, useState, useCallback, useEffect } from "react";
import { ModalContext } from "../../../utils/modal/ModalProvider";
import axios from "../../../utils/api/axios";
import styles from './ServiceUserSelectModal.module.css';
import { useInfiniteScroll } from "./useInfiniteScroll";
import { InnocodeButton, InnocodeDatatable } from '@intsoft/innocode-core';
import { IAM_SERVER_TENANT_USER } from "../../../utils/Config";

const fetchAvailableUsers = async ({ tenantId, serviceId, page, size = 15 }) => {
    const response = await axios.post(`${IAM_SERVER_TENANT_USER}/available-users`, {
        tenantId: tenantId,
        serviceId: serviceId,
        page: page,
        size: size
    });
    return response.data.data;
};

/**
 * 서비스 관리자 등록을 위한 사용자 선택 모달
 * @param {Object} service - 서비스 정보
 * @param {Function} onConfirm - 선택 완료 콜백 (선택된 유저 배열 전달)
 * @param {Function} onClose - 모달 닫기 콜백
 */
export const ServiceUserSelectModal = ({ service, onConfirm, onClose }) => {
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [isSelectAll, setIsSelectAll] = useState(false);
    const [tableResetKey, setTableResetKey] = useState(0);

    // useInfiniteScroll 훅을 사용하되, 파라미터를 tenantId와 serviceId로 변경
    const { items: availableUsers, loading, hasMore, lastItemRef, search } = useInfiniteScroll(
        fetchAvailableUsers,
        { tenantId: service.createdBy, serviceId: service.serviceId },
        { size: 15, getKey: (u) => u.tenantUserId }
    );

    useEffect(() => {
        search(""); // 최초 로드
    }, []);

    // 전체선택 핸들러
    useEffect(() => {
        if (isSelectAll) {
            setSelectedUsers(availableUsers);
        } else {
            setSelectedUsers([]);
        }
    }, [isSelectAll, availableUsers]);

    // 체크박스 선택 핸들러
    const handleRowSelectionChange = useCallback((row, index, isChecked) => {
        if (isChecked) {
            setSelectedUsers(prev => [...prev, row]);
        } else {
            setSelectedUsers(prev => prev.filter(u => u.tenantUserId !== row.tenantUserId));
        }
    }, []);

    // 확인 버튼
    const handleConfirm = () => {
        onConfirm(selectedUsers);
        onClose();
    };

    // 테이블 컬럼 정의
    const columns = [
        {
            id: 'user',
            header: '테넌트 유저',
            accessorKey: 'tenantUserId',
            enableSorting: false,
            cell: ({ row }) => (
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <img
                        src={row.profileImgUrl || "https://cdn-icons-png.flaticon.com/512/149/149071.png"}
                        alt="프로필 이미지"
                        style={{ width: "28px", height: "28px", borderRadius: "50%", objectFit: "cover" }}
                    />
                    <span className={styles.cellText}>{`${row.profileName} (${row.unifiedProfileId})`}</span>
                </div>
            ),
        },
    ];

    return (
        <div style={{}}>
            <div className={styles.tableSection}>
                <div className={styles.tableSectionHeader}>
                    <span className={styles.tableSectionTitle}>
                        선택 가능한 유저 {selectedUsers.length > 0 && `(${selectedUsers.length}명 선택됨)`}
                    </span>
                </div>
                <div className={styles.tableWrapper}>
                    <InnocodeDatatable
                        key={`user-select-${tableResetKey}-${isSelectAll}`}
                        className={styles.datatable}
                        data={availableUsers}
                        columns={columns}
                        enableCustomHeader={true}
                        headerCheckboxSelectsAll={isSelectAll}
                        manualPagination={false}
                        enableSorting={false}
                        enableCheckbox={true}
                        isLoading={loading}
                        emptyMessage="선택 가능한 유저가 없습니다."
                        onRowSelectionChange={handleRowSelectionChange}
                        onSelectAllChange={(isSelected) => setIsSelectAll(isSelected)}
                        enableInfiniteScroll={true}
                    />
                </div>
            </div>

            <div className={styles.actionButtonWrapper}>
                <InnocodeButton
                    className={styles.actionButton}
                    theme="opacity"
                    designType="primary"
                    size="lg"
                    onClick={onClose}
                >
                    취소
                </InnocodeButton>
                <InnocodeButton
                    className={styles.actionButton}
                    theme="solid"
                    size="lg"
                    onClick={handleConfirm}
                    disabled={selectedUsers.length === 0}
                >
                    확인 ({selectedUsers.length})
                </InnocodeButton>
            </div>
        </div>
    );
};
